import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography } from "antd";
import { SbModal, SbNotification, SbButton, SbSelect, SbInput, SbTooltip, TooltipTriggerStyled, ReloadOutlined, SearchOutlined, TeamOutlined } from "uiCore";
import SbEmpty from "uiCore/SbEmpty/SbEmpty";
import SbTour from "uiCore/SbTour/SbTour";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import { CheckCircleOutlined } from "uiCore/SbIcons/SbIcons";
import PeoplePlusAiIcon from "Components/SBLayoutIcon/PeoplePlusAiIcon";
import Permission from "Components/Permission";
import { EventSourcePolyfill } from "event-source-polyfill";
import { getPAICInferredJDs, getPAICProjects, createPAICProject, createPAICProjectRun, reinitiatePAICProjectRun, getPAICProjectStatus } from "Api/apiService";
import { API_ENDPOINTS } from "config/ApiConfig";
import useAuthStore from "Store/useAuthStore";
import analytics from "Utils/analytics";
import usePAICStore from "Store/usePAICStore";
import useTourStore from "Store/useTourStore";
import PAICRequisitionTableV2 from "./PAICRequisitionTableV2";
import ManageGroupsModal from "./ManageGroupsModal";
import { SEARCHABLE_FIELDS, INITIAL_FILTERS, normalize, PAIC_PROJECT_STATUS, PAIC_PROJECT_SOURCE, PAIC_BUTTON_LABELS, PAIC_MODAL_CONTENT, PAIC_ERROR_CODES, PAIC_ERROR_MESSAGES, PAIC_SUCCESS_MESSAGES, PAIC_WARNING_MESSAGES, handlePAICErrorCode } from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import { TOUR_MODULE_KEY, getTourSteps, CROSS_PAGE_TOUR_PHASE } from "./PeoplePlusAIV2.constants";
import { PageContainer, HeaderSection, HeaderTopRow, TitleGroup, TitleRow, RefreshButton, HeaderActionsPanel, ManageGroupsBtnWrapper, TopActionsRow, SearchWrapper, SearchIconBox, ControlsRow, GroupControlWrapper, CreateGroupRow, CreateGroupLabel, EvaluateButtonWrapper, PAICProgressPanel, PAICProgressHeader, PAICProgressTrack, PAICProgressFill, PAICProgressMeta, OptionRow, OptionText, BadgeGroup, SourceBadge, StatusBadge, TableSection, PaicModalGlobal } from "./PeoplePlusAIV2.styles";

const { Title, Text } = Typography;

const TOOLTIP_GROUP_NAME_INPUT =
  "Type a name for your new PAIC group and press Enter to create it. Choose a name that identifies the set of roles you're analysing (e.g. 'HR Function Q2 2026').";
const TOOLTIP_SELECT_GROUP_DROPDOWN =
  "View and switch between your existing PAIC groups. Each group contains a set of roles selected for AI impact analysis.";
const TOOLTIP_SEARCH_FILTER =
  "Search or filter the list of available roles to include in your PAIC group.";
const TOOLTIP_EVALUATE_PAIC =
  "Start the AI evaluation for the selected group. The system will analyse tasks across all roles and identify AI agent opportunities. This takes 10–45 minutes depending on group size.";
const TOOLTIP_RE_EVALUATE_PAIC =
  "The previous evaluation failed. Click to retry — the system will run the full AI analysis again.";
const TOOLTIP_VIEW_PAIC =
  "The evaluation is complete. Click to view the identified AI agents and select which ones to include in your workforce redesign.";
const TOOLTIP_PROCESSING_PAIC =
  "The AI evaluation is currently running. Please wait — this typically takes 10–45 minutes.";
const TOOLTIP_STATUS_CREATED =
  "This group has been created but the AI evaluation has not yet been started. Click 'Evaluate PAIC Potential' to begin.";
const TOOLTIP_STATUS_PROCESSING =
  "The AI evaluation is running in the background. You'll be notified when it completes.";
const TOOLTIP_STATUS_COMPLETED =
  "The evaluation is complete. Click 'View PAIC Potential' to see the identified AI agents.";
const TOOLTIP_STATUS_FAILED =
  "The evaluation encountered an error. Click 'Re-Evaluate PAIC Potential' to retry.";
const TOOLTIP_MANAGE_GROUPS =
  "Open the Manage Groups panel to view, archive, restore, or delegate your PAIC groups.";
const TOOLTIP_AGENT_LIBRARY =
  "Browse every AI agent across all your groups in one place — view its group, status, and documents.";

const STATUS_BADGE_TOOLTIPS = {
  [PAIC_PROJECT_STATUS.CREATED]: TOOLTIP_STATUS_CREATED,
  [PAIC_PROJECT_STATUS.PROCESSING]: TOOLTIP_STATUS_PROCESSING,
  [PAIC_PROJECT_STATUS.COMPLETED]: TOOLTIP_STATUS_COMPLETED,
  [PAIC_PROJECT_STATUS.FAILED]: TOOLTIP_STATUS_FAILED,
};

const normalizeProjectsResponse = (response) => {
  if (!response) return { owned: [], delegated: [], shared: [] };

  if (response.owned_projects || response.delegated_projects || response.shared_projects) {
    return {
      owned: response.owned_projects || [],
      delegated: response.delegated_projects || [],
      shared: response.shared_projects || [],
    };
  }

  if (Array.isArray(response)) {
    return { owned: response, delegated: [], shared: [] };
  }

  return { owned: [], delegated: [], shared: [] };
};

const mergeAllProjects = (owned = [], delegated = [], shared = []) => [
  ...owned.map((p) => ({ ...p, _source: PAIC_PROJECT_SOURCE.OWNED })),
  ...delegated.map((p) => ({ ...p, _source: PAIC_PROJECT_SOURCE.DELEGATED })),
  ...shared.map((p) => ({ ...p, _source: PAIC_PROJECT_SOURCE.SHARED })),
];

const SOURCE_BADGE_LABELS = {
  [PAIC_PROJECT_SOURCE.OWNED]: "Owner",
  [PAIC_PROJECT_SOURCE.DELEGATED]: "Co-creator",
  [PAIC_PROJECT_SOURCE.SHARED]: "Shared",
};

const formatProjectOptions = (projects = []) =>
  projects.map((project) => ({
    value: project.project_name,
    label: (
      <OptionRow>
        <OptionText>{project.project_name}</OptionText>
        <BadgeGroup>
          {project._source && (
            <SourceBadge $source={project._source}>
              {SOURCE_BADGE_LABELS[project._source] || ""}
            </SourceBadge>
          )}
          <SbTooltip content={STATUS_BADGE_TOOLTIPS[project.status]}>
            <StatusBadge status={project.status}>{project.status}</StatusBadge>
          </SbTooltip>
        </BadgeGroup>
      </OptionRow>
    ),
    rawLabel: project.project_name,
    status: project.status,
  }));

const extractPAICTaskId = (response = {}) =>
  response.task_id ||
  response.run_id ||
  response.data?.task_id ||
  response.data?.run_id ||
  response.project_run?.task_id ||
  response.project_run?.run_id ||
  null;

const isPAICTerminalEvent = (payload = {}) => {
  const event = String(payload.event || "").toLowerCase();
  const status = String(payload.status || "").toLowerCase();
  return Number(payload.progress) >= 100 || event.includes("complete") || status.includes("complete") || status.includes("success");
};

const isPAICFailureEvent = (payload = {}) => {
  const event = String(payload.event || "").toLowerCase();
  const status = String(payload.status || "").toLowerCase();
  return Boolean(payload.error) || event.includes("fail") || status.includes("fail") || status.includes("error");
};

const PeoplePlusAIV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const email = useAuthStore((state) => state.email);
  // Verbatim from the V1 landing page these events used to fire from
  // (Pages/People+AI/SbPeoplePlusAI.jsx), which RouteConfigV2 no longer
  // renders — so this whole group of events had gone silent.
  const analyticsFrom = "people_plus_ai_main";
  const refreshToken = useAuthStore((state) => state.refreshToken);
  const accessToken = useAuthStore((state) => state.accessToken) || localStorage.getItem("access_token");

  const setProjectId = usePAICStore((state) => state.setProjectId);
  const loading = usePAICStore((state) => state.loading);
  const setLoading = usePAICStore((state) => state.setLoading);
  const groupName = usePAICStore((state) => state.groupName);
  const setGroupName = usePAICStore((state) => state.setGroupName);
  const setProjectSource = usePAICStore((state) => state.setProjectSource);
  const resetProject = usePAICStore((state) => state.resetProject);

  const [jobData, setJobData] = useState([]);
  // True once the roles have been fetched at least once — so the tour's empty
  // state doesn't flash on the first render (before the fetch runs).
  const [hasFetchedRoles, setHasFetchedRoles] = useState(false);
  const [projectGroupOptions, setProjectGroupOptions] = useState([]);
  const [paicProjects, setPAICProjects] = useState([]);
  const [ownedProjects, setOwnedProjects] = useState([]);
  const [delegatedProjects, setDelegatedProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pageSearchTerm, setPageSearchTerm] = useState("");
  const [selectedProjectGroup, setSelectedProjectGroup] = useState(null);
  const selectedProjectGroupRef = useRef(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [paicTask, setPaicTask] = useState(null);
  const [paicProgress, setPaicProgress] = useState(null);
  const paicEventSourceRef = useRef(null);
  const shouldRestoreSelectedGroup = Boolean(
    location.state?.preserveSelectedGroup,
  );

  // ── Guided tour ─────────────────────────────────────────────────────────────
  // Refs point each spotlight step at a real element. Evaluate and View PAIC are
  // the same button (its label changes with status), so both keys share one ref.
  const rolesTableRef = useRef(null);
  const groupControlRef = useRef(null);
  const evaluateRef = useRef(null);
  const manageGroupsRef = useRef(null);
  const emptyStateRef = useRef(null);
  const tourTargetRefs = {
    rolesTable: rolesTableRef,
    groupControl: groupControlRef,
    evaluate: evaluateRef,
    viewPaic: evaluateRef,
    manageGroups: manageGroupsRef,
    emptyState: emptyStateRef,
  };
  // Cross-page baton: the tour hops to the Group Agents route for the Stats /
  // Version / Doc-Type steps, then returns here to finish. autoLaunch is off
  // while a hop is in flight so the tour doesn't restart on remount.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const clearCrossPageTourPhase = useTourStore((s) => s.clearCrossPageTourPhase);
  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin,
    goToStep: goToTourStep,
    close: closeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY, autoLaunch: !crossPageTourPhase });

  const showSuccessNotification = (messageText, descriptionText = "") => {
    SbNotification.success(messageText, descriptionText);
  };

  const showErrorNotification = (messageText, descriptionText = "") => {
    SbNotification.error(messageText, descriptionText);
  };

  const showWarningNotification = (messageText, descriptionText = "") => {
    SbNotification.warning(messageText, descriptionText);
  };

  const fetchInferredJobsRequisitionView = async () => {
    setLoading(true);

    try {
      const response = await getPAICInferredJDs({ email });
      setJobData(response || []);
    } catch (fetchError) {
      if (!handlePAICErrorCode(fetchError, showErrorNotification)) {
        showErrorNotification(PAIC_ERROR_MESSAGES.LOAD_ROLES_FAILED, PAIC_ERROR_MESSAGES.FETCH_JOBS_FAILED);
      }
    } finally {
      setLoading(false);
      setHasFetchedRoles(true);
    }
  };

  const fetchPAICProjects = useCallback(async ({ syncSelectedProject = true, restoreFromStore = false } = {}) => {
    setLoading(true);

    try {
      const response = await getPAICProjects();
      const { owned, delegated, shared } = normalizeProjectsResponse(response);

      setOwnedProjects(owned);
      setDelegatedProjects(delegated);
      setSharedProjects(shared);

      const allProjects = mergeAllProjects(owned, delegated, shared);
      setProjectGroupOptions(formatProjectOptions(allProjects));
      setPAICProjects(allProjects);

      if (syncSelectedProject) {
        const currentSelectedProject = selectedProjectGroupRef.current;

        if (currentSelectedProject) {
          const latestSelectedProject = allProjects.find(
            (project) => project.project_name === currentSelectedProject,
          );

          if (!latestSelectedProject) {
            setFilters(INITIAL_FILTERS);
            setSelectedProjectGroup(null);
          } else if (latestSelectedProject.filters) {
            setFilters(latestSelectedProject.filters);
          }
        } else if (restoreFromStore && groupName) {
          const persistedProject = allProjects.find(
            (project) => project.project_name === groupName,
          );

          if (persistedProject) {
            setSelectedProjectGroup(persistedProject.project_name);
            if (persistedProject.filters) {
              setFilters(persistedProject.filters);
            }
          }
        }
      }

      return { owned, delegated, shared, allProjects };
    } catch (fetchError) {
      if (!handlePAICErrorCode(fetchError, showErrorNotification)) {
        showErrorNotification(PAIC_ERROR_MESSAGES.LOAD_GROUPS_FAILED, PAIC_ERROR_MESSAGES.FETCH_GROUPS_FAILED);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, groupName]);

  const handleGroupsDropdownOpen = async (open) => {
    if (open) {
      await fetchPAICProjects();
    }
  };

  const handleOpenManageGroups = useCallback(() => {
    setManageGroupsOpen(true);
    fetchPAICProjects();
  }, [fetchPAICProjects]);

  const handleSelectGroupFromModal = useCallback(
    (project) => {
      analytics.trackPAICSelectGroup({
        from: analyticsFrom,
        email,
        groupName: project.project_name,
        description: "Selected PAIC group",
      });
      setSelectedProjectGroup(project.project_name);
      if (project.filters) {
        setFilters(project.filters);
      }
    },
    [email, analyticsFrom]
  );

  const handleCreateGroup = async () => {
    const groupNameToCreate = newGroupName.trim();

    if (!groupNameToCreate) return;

    analytics.trackPAICCreateGroup({
      from: analyticsFrom,
      email,
      groupName: groupNameToCreate,
      rolesCount: filteredJobs.length,
      description: `Created group with ${filteredJobs.length} roles`,
    });

    const tempGroupName = groupNameToCreate;
    setNewGroupName("");
    setLoading(true);

    let isDuplicateJobIds = false;

    try {
      const payload = {
        project_name: groupNameToCreate,
        job_ids: filteredJobs.map((job) => job.sb_unique_id),
        filters,
      };

      await createPAICProject(payload);
      await fetchPAICProjects();

      showSuccessNotification("Group created successfully");
    } catch (createError) {
      const errorCode = createError?.response?.data?.error_code;
      const duplicateProjectId = createError?.response?.data?.sb_project_unique_id;
      const apiMessage = createError?.response?.data?.message;

      if (errorCode === PAIC_ERROR_CODES.DUPLICATE_PROJECT_NAME) {
        showErrorNotification(PAIC_ERROR_MESSAGES.DUPLICATE_PROJECT, apiMessage || `${tempGroupName} already exists, please give a different name`);
        setNewGroupName(tempGroupName);
      } else if (errorCode === PAIC_ERROR_CODES.DUPLICATE_JOB_IDS) {
        isDuplicateJobIds = true;
        showWarningNotification(PAIC_WARNING_MESSAGES.DUPLICATE_JOB_SELECTION, apiMessage || PAIC_WARNING_MESSAGES.GROUP_EXISTS_WITH_JDS);

        setTimeout(async () => {
          setFilters(INITIAL_FILTERS);

          const refreshedProjects = await fetchPAICProjects({ syncSelectedProject: false });
          const allProjects = refreshedProjects?.allProjects || [];

          const existingProject = allProjects.find(
            (project) => project.sb_project_unique_id === duplicateProjectId,
          );

          if (existingProject) {
            setSelectedProjectGroup(existingProject.project_name);
          }

          setLoading(false);
        }, 3000);
      } else {
        setNewGroupName(tempGroupName);
        if (!handlePAICErrorCode(createError, showErrorNotification)) {
          showErrorNotification(PAIC_ERROR_MESSAGES.CREATE_GROUP_FAILED, PAIC_ERROR_MESSAGES.SOMETHING_WENT_WRONG);
        }
      }
    } finally {
      if (!isDuplicateJobIds) {
        setLoading(false);
      }
    }
  };

  const handlePAICPotentialClick = async () => {
    if (!selectedProjectGroup) return;

    // V1 fired these from inside the two request branches. The button label is
    // derived from the same project status those branches switch on, so it is
    // the one place that already knows which of the three this click is.
    if (evaluateButtonLabel === PAIC_BUTTON_LABELS.VIEW_PAIC) {
      analytics.trackPAICViewPotential({
        from: analyticsFrom,
        email,
        groupName: selectedProjectGroup,
        description: "Opened PAIC potential",
      });
    } else if (evaluateButtonLabel === PAIC_BUTTON_LABELS.RE_EVALUATE_PAIC) {
      analytics.trackPAICReEvaluateInitiated({
        from: analyticsFrom,
        email,
        groupName: selectedProjectGroup,
        description: "Initiated PAIC re-evaluation",
      });
    } else {
      analytics.trackPAICEvaluateInitiated({
        from: analyticsFrom,
        email,
        groupName: selectedProjectGroup,
        description: "Initiated PAIC evaluation",
      });
    }

    const selectedProject = paicProjects.find(
      (project) => project.project_name === selectedProjectGroup,
    );

    if (!selectedProject || !selectedProject.sb_project_unique_id) {
      showErrorNotification(
        "Invalid group selection",
        "Selected project details could not be found.",
      );
      return;
    }

    if (selectedProject.status === PAIC_PROJECT_STATUS.COMPLETED) {
      setProjectId(selectedProject.sb_project_unique_id);
      setGroupName(selectedProject.project_name);
      setProjectSource(selectedProject._source || PAIC_PROJECT_SOURCE.OWNED);
      navigate(PAIC_ROUTES.GROUP_AGENTS);
      return;
    }

    if (selectedProject.status === PAIC_PROJECT_STATUS.PROCESSING) {
      return;
    }

    if (selectedProject.status === PAIC_PROJECT_STATUS.CREATED) {
      SbModal.confirm({
        title: PAIC_MODAL_CONTENT.START_EVALUATION_TITLE,
        content: PAIC_MODAL_CONTENT.START_EVALUATION_CONTENT,
        okText: PAIC_BUTTON_LABELS.PROCEED,
        cancelText: PAIC_BUTTON_LABELS.CANCEL,
        centered: true,
        className: 'paic-confirm-modal',
        onOk: async () => {
          setLoading(true);
          try {
            const payload = {
              sb_project_unique_id: selectedProject.sb_project_unique_id,
            };
            const response = await createPAICProjectRun(payload, refreshToken);
            const taskId = extractPAICTaskId(response);

            if (response?.status === PAIC_PROJECT_STATUS.PROCESSING) {
              showSuccessNotification("PAIC evaluation initiated");
            } else {
              showSuccessNotification("PAIC evaluation started");
            }

            if (taskId) {
              setPaicTask({
                taskId,
                projectId: selectedProject.sb_project_unique_id,
                projectName: selectedProject.project_name,
              });
              setPaicProgress({ progress: 0, status: "Starting PAIC evaluation..." });
            }
          } catch (runError) {
            if (!handlePAICErrorCode(runError, showErrorNotification)) {
              showErrorNotification(PAIC_ERROR_MESSAGES.EVALUATION_FAILED, PAIC_ERROR_MESSAGES.UNABLE_TO_START_EVALUATION);
            }
          } finally {
            await fetchPAICProjects();
            setLoading(false);
          }
        },
      });
    }

    if (selectedProject.status === PAIC_PROJECT_STATUS.FAILED) {
      SbModal.confirm({
        title: PAIC_MODAL_CONTENT.RE_RUN_EVALUATION_TITLE,
        content: PAIC_MODAL_CONTENT.RE_RUN_EVALUATION_CONTENT,
        okText: PAIC_BUTTON_LABELS.PROCEED,
        cancelText: PAIC_BUTTON_LABELS.CANCEL,
        centered: true,
        className: 'paic-confirm-modal',
        onOk: async () => {
          setLoading(true);
          try {
            const payload = {
              sb_project_unique_id: selectedProject.sb_project_unique_id,
            };
            const response = await reinitiatePAICProjectRun(payload, refreshToken);
            const taskId = extractPAICTaskId(response);
            if (response?.status === PAIC_PROJECT_STATUS.PROCESSING) {
              showSuccessNotification("PAIC re-evaluation initiated");
            } else {
              showSuccessNotification("PAIC re-evaluation started");
            }

            if (taskId) {
              setPaicTask({
                taskId,
                projectId: selectedProject.sb_project_unique_id,
                projectName: selectedProject.project_name,
              });
              setPaicProgress({ progress: 0, status: "Restarting PAIC evaluation..." });
            }
          } catch (runError) {
            if (!handlePAICErrorCode(runError, showErrorNotification)) {
              showErrorNotification(PAIC_ERROR_MESSAGES.EVALUATION_FAILED, PAIC_ERROR_MESSAGES.UNABLE_TO_START_EVALUATION);
            }
          } finally {
            await fetchPAICProjects();
            setLoading(false);
          }
        },
      });
    }
  };

  useEffect(() => {
    if (!shouldRestoreSelectedGroup) {
      setFilters(INITIAL_FILTERS);
      setPageSearchTerm("");
      setSelectedProjectGroup(null);
      resetProject();
    }

    fetchInferredJobsRequisitionView();
    fetchPAICProjects({ restoreFromStore: shouldRestoreSelectedGroup });

    if (shouldRestoreSelectedGroup) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    selectedProjectGroupRef.current = selectedProjectGroup;
  }, [selectedProjectGroup]);

  useEffect(() => {
    if (!paicTask?.taskId || !accessToken) return undefined;

    let cancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 5;

    const closeStream = () => {
      if (paicEventSourceRef.current) {
        paicEventSourceRef.current.close();
        paicEventSourceRef.current = null;
      }
    };

    const finishTask = async (payload, failed = false) => {
      closeStream();
      setPaicProgress({
        ...payload,
        progress: failed ? Number(payload.progress || 100) : 100,
        failed,
        status: payload.status || (failed ? "PAIC evaluation failed" : "PAIC evaluation completed"),
      });

      if (failed) {
        showErrorNotification(
          "PAIC Evaluation Failed",
          payload.error || payload.message || payload.status || "The background task failed. Please review the group status and retry."
        );
      } else {
        showSuccessNotification("PAIC evaluation completed", "Group status has been refreshed.");
      }

      await fetchPAICProjects();

      if (paicTask.projectId) {
        try {
          await getPAICProjectStatus(paicTask.projectId);
        } catch (statusError) {
          console.warn("Unable to refresh PAIC project status after task completion:", statusError);
        }
      }

      setTimeout(() => {
        if (!cancelled) {
          setPaicTask(null);
          setPaicProgress(null);
        }
      }, failed ? 8000 : 5000);
    };

    const connect = () => {
      if (cancelled) return;
      closeStream();

      const eventSource = new EventSourcePolyfill(
        `${API_ENDPOINTS.RAPPID_MQ_EVENTS}${paicTask.taskId}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          heartbeatTimeout: 60 * 60 * 1000,
        }
      );

      paicEventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        if (cancelled) return;

        try {
          const payload = JSON.parse(event.data);

          if (payload.event === "key_does_not_exist") {
            closeStream();
            if (retryCount < MAX_RETRIES) {
              retryCount += 1;
              setPaicProgress((prev) => ({
                ...(prev || {}),
                progress: prev?.progress || 0,
                status: "Preparing PAIC progress stream...",
              }));
              setTimeout(connect, 2000);
            } else {
              finishTask(
                {
                  progress: 0,
                  status: "Unable to connect to PAIC progress stream",
                  error: "The task stream was not available. The project status will still refresh from the server.",
                },
                true
              );
            }
            return;
          }

          setPaicProgress({
            ...payload,
            progress: Number(payload.progress || 0),
            failed: isPAICFailureEvent(payload),
          });

          if (isPAICFailureEvent(payload)) {
            finishTask(payload, true);
            return;
          }

          if (isPAICTerminalEvent(payload)) {
            finishTask(payload, false);
          }
        } catch (parseError) {
          console.error("Unable to parse PAIC task event:", parseError);
        }
      };

      eventSource.onerror = () => {
        closeStream();
        if (!cancelled && retryCount < MAX_RETRIES) {
          retryCount += 1;
          setTimeout(connect, 3000);
        } else if (!cancelled) {
          finishTask(
            {
              progress: 0,
              status: "Unable to maintain PAIC progress stream",
              error: "The progress stream disconnected before completion. Please refresh the group status if needed.",
            },
            true
          );
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      closeStream();
    };
  }, [paicTask?.taskId, paicTask?.projectId, accessToken, fetchPAICProjects]);

  const areFiltersEqual = (filters1, filters2) => {
    if (!filters1 || !filters2) return false;

    const keys1 = Object.keys(filters1);
    const keys2 = Object.keys(filters2);
    const allKeys = new Set([...keys1, ...keys2]);

    for (const key of allKeys) {
      const val1 = filters1[key] || [];
      const val2 = filters2[key] || [];

      if (val1.length !== val2.length) return false;

      const sorted1 = [...val1].map(normalize).sort();
      const sorted2 = [...val2].map(normalize).sort();

      if (sorted1.some((value, index) => value !== sorted2[index])) {
        return false;
      }
    }

    return true;
  };

  const activeFilterCount = Object.values(filters).reduce(
    (acc, curr) => acc + (Array.isArray(curr) ? curr.length : 0),
    0,
  );

  const matchingProject = useMemo(() => {
    if (!paicProjects.length || activeFilterCount === 0) {
      return null;
    }

    return paicProjects.find(
      (project) => project.filters && areFiltersEqual(filters, project.filters),
    );
  }, [filters, paicProjects, activeFilterCount]);

  useEffect(() => {
    if (matchingProject) {
      if (selectedProjectGroup !== matchingProject.project_name) {
        setSelectedProjectGroup(matchingProject.project_name);
      }
    } else if (activeFilterCount > 0 && selectedProjectGroup !== null) {
      setSelectedProjectGroup(null);
    }
  }, [matchingProject, activeFilterCount, selectedProjectGroup]);

  const filteredJobs = useMemo(() => {
    const normalizedSearch = normalize(pageSearchTerm);

    let result = (jobData || []).filter((job) => {
      const matchesSearch =
        !pageSearchTerm.trim() ||
        SEARCHABLE_FIELDS.some((field) =>
          normalize(job?.[field]).includes(normalizedSearch),
        );

      const matchesTableFilters = Object.entries(filters).every(
        ([key, selectedValues]) => {
          if (!selectedValues?.length) return true;

          return selectedValues
            .map((value) => normalize(value))
            .includes(normalize(job?.[key]));
        },
      );

      return matchesSearch && matchesTableFilters;
    });

    if (selectedProjectGroup) {
      const selectedProject = paicProjects.find(
        (project) => project.project_name === selectedProjectGroup,
      );

      if (selectedProject && selectedProject.filters) {
        const storedFilters = selectedProject.filters;
        result = result.filter((job) =>
          Object.keys(storedFilters).every((key) => {
            const filterValues = storedFilters[key];
            if (!Array.isArray(filterValues) || filterValues.length === 0)
              return true;
            return filterValues.map(normalize).includes(normalize(job[key]));
          }),
        );
      }
    }

    return result;
  }, [jobData, pageSearchTerm, filters, selectedProjectGroup, paicProjects]);

  const clearAllFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPageSearchTerm("");
    setSelectedProjectGroup(null);
  };

  const selectedProject = paicProjects.find(
    (project) => project.project_name === selectedProjectGroup,
  );

  const evaluateButtonLabel = useMemo(() => {
    if (selectedProject?.status === PAIC_PROJECT_STATUS.PROCESSING) {
      return PAIC_BUTTON_LABELS.PROCESSING_PAIC;
    }

    if (selectedProject?.status === PAIC_PROJECT_STATUS.COMPLETED) {
      return PAIC_BUTTON_LABELS.VIEW_PAIC;
    }

    if (selectedProject?.status === PAIC_PROJECT_STATUS.FAILED) {
      return PAIC_BUTTON_LABELS.RE_EVALUATE_PAIC;
    }

    return PAIC_BUTTON_LABELS.EVALUATE_PAIC;
  }, [selectedProject]);

  const isEvaluateDisabled = useMemo(() => {
    if (loading) return true;
    if (!selectedProjectGroup) return true;
    if (selectedProject?.status === PAIC_PROJECT_STATUS.PROCESSING) return true;
    return false;
  }, [loading, selectedProjectGroup, selectedProject]);

  const evaluateButtonTooltip = useMemo(() => {
    if (selectedProject?.status === PAIC_PROJECT_STATUS.PROCESSING) {
      return TOOLTIP_PROCESSING_PAIC;
    }

    if (selectedProject?.status === PAIC_PROJECT_STATUS.COMPLETED) {
      return TOOLTIP_VIEW_PAIC;
    }

    if (selectedProject?.status === PAIC_PROJECT_STATUS.FAILED) {
      return TOOLTIP_RE_EVALUATE_PAIC;
    }

    return TOOLTIP_EVALUATE_PAIC;
  }, [selectedProject]);

  // Empty when the roles table has no rows (mirrors Jobs Library's isLibraryEmpty).
  // Gated on hasFetchedRoles so the empty state doesn't flash before the first fetch.
  const isRolesEmpty = hasFetchedRoles && !loading && (jobData?.length ?? 0) === 0;
  // Stage 1 shows the main-page steps only; the Group Agents steps (handoffToPage)
  // live on the /people+ai/pac-group-agents route and are added in a later pass.
  const tourSteps = getTourSteps(isRolesEmpty).filter((step) => !step.handoffToPage);

  const closeTour = () => {
    markSeen();
    closeMainTour();
  };

  // Finish greeting primary → focus the group name / select control.
  const finishTour = () => {
    markSeen();
    closeMainTour();
    groupControlRef.current?.querySelector("input")?.focus();
  };

  // Advancing into Finish detours through the Group Agents route for the
  // Stats/Version/Doc-Type steps. If any Completed group exists, auto-select it
  // and open its Group Agents screen so the tour can show those steps; that page
  // navigates back here to resume at Finish. With no Completed group, just finish.
  const changeTourStep = (step) => {
    const goingToFinish = step === tourSteps.length - 1;
    if (goingToFinish && step > tourStep && !isRolesEmpty) {
      const completedProject = paicProjects.find(
        (project) => project.status === PAIC_PROJECT_STATUS.COMPLETED,
      );
      if (completedProject) {
        setSelectedProjectGroup(completedProject.project_name);
        setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.GROUP_AGENTS);
        setProjectId(completedProject.sb_project_unique_id);
        setGroupName(completedProject.project_name);
        setProjectSource(completedProject._source || PAIC_PROJECT_SOURCE.OWNED);
        navigate(PAIC_ROUTES.GROUP_AGENTS);
        return;
      }
    }
    goToTourStep(step);
  };

  // Returning from the Group Agents leg → resume at Finish once settled. Wait for
  // the roles to finish loading (not the transient empty state on mount), else
  // the branch has fewer steps and Finish lands on the wrong index.
  useEffect(() => {
    if (crossPageTourPhase !== CROSS_PAGE_TOUR_PHASE.FINISH || loading || !hasFetchedRoles) return;
    clearCrossPageTourPhase();
    begin(tourSteps.length - 1); // Finish is the last on-page step
  }, [crossPageTourPhase, loading, hasFetchedRoles, tourSteps.length, clearCrossPageTourPhase, begin]);

  const {
    steps: builtTourSteps,
    actionsRender: renderTourActions,
    indicatorsRender: renderTourIndicators,
  } = buildTourSteps(tourSteps, {
    resolveTarget: (step) => tourTargetRefs[step.key]?.current,
    greeting: {
      icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <PeoplePlusAiIcon />),
      onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
      onSecondary: () => closeTour(),
    },
  });

  return (
    <>
    <PaicModalGlobal />
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <TitleGroup>
            <TitleRow>
              <Title level={3}>People+AI Collaboration (PAIC)</Title>

              <RefreshButton
                type="button"
                onClick={() => {
                  analytics.trackPAICRefreshData({
                    from: analyticsFrom,
                    email,
                    description: "User refreshed PAIC data",
                  });
                  fetchInferredJobsRequisitionView();
                }}
                title="Refresh Data"
                aria-label="Refresh Data"
              >
                <ReloadOutlined />
              </RefreshButton>
            </TitleRow>

            <Text type="secondary">
              This module helps you identify, prioritize, and assign AI agents
              to collaborate with your workforce.
              <br />
              1. Select the roles you want AI agents to work with. Name this
              group.
              <br />
              2. Click "Evaluate PAIC Potential" to continue.
            </Text>
          </TitleGroup>

          <HeaderActionsPanel>
            <TopActionsRow>
              <SbTooltip content={TOOLTIP_SEARCH_FILTER}>
                <SearchWrapper>
                  <SbInput
                    placeholder="Search role by keyword, skill, etc."
                    value={pageSearchTerm}
                    onChange={(e) => setPageSearchTerm(e.target.value)}
                    suffix={
                      <SearchIconBox>
                        <SearchOutlined />
                      </SearchIconBox>
                    }
                  />
                </SearchWrapper>
              </SbTooltip>
            </TopActionsRow>

            <ControlsRow>
              <Permission resource="people_plus_ai" actions={["view_agent_library"]}>
                <SbTooltip content={TOOLTIP_AGENT_LIBRARY}>
                  <TooltipTriggerStyled>
                    <SbButton
                      buttonType="secondary"
                      onClick={() => navigate(PAIC_ROUTES.AGENT_LIBRARY)}
                    >
                      Agent Library
                    </SbButton>
                  </TooltipTriggerStyled>
                </SbTooltip>
              </Permission>

              <SbTooltip content={TOOLTIP_MANAGE_GROUPS}>
                <ManageGroupsBtnWrapper ref={manageGroupsRef}>
                  <button
                    type="button"
                    onClick={handleOpenManageGroups}
                    aria-label="Manage Groups"
                    title="Manage Groups"
                  >
                    <TeamOutlined />
                    Manage Groups
                  </button>
                </ManageGroupsBtnWrapper>
              </SbTooltip>

              <GroupControlWrapper ref={groupControlRef}>
                {activeFilterCount > 0 && !matchingProject ? (
                  <CreateGroupRow>
                    <CreateGroupLabel>Create New Group:</CreateGroupLabel>
                    <SbTooltip content={TOOLTIP_GROUP_NAME_INPUT}>
                      <TooltipTriggerStyled>
                        <SbInput
                          placeholder="Give it a name and click enter"
                          value={newGroupName}
                          onChange={(e) => setNewGroupName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCreateGroup();
                            }
                          }}
                        />
                      </TooltipTriggerStyled>
                    </SbTooltip>
                  </CreateGroupRow>
                ) : (
                  <SbTooltip content={TOOLTIP_SELECT_GROUP_DROPDOWN}>
                    <TooltipTriggerStyled>
                      <SbSelect
                        options={projectGroupOptions}
                        value={selectedProjectGroup}
                        onDropdownVisibleChange={handleGroupsDropdownOpen}
                        onChange={(value) => {
                          if (!value) {
                            clearAllFilters();
                          } else {
                            setFilters(INITIAL_FILTERS);
                            setSelectedProjectGroup(value);

                            const proj = paicProjects.find(
                              (project) => project.project_name === value,
                            );
                            if (proj && proj.filters) {
                              setFilters(proj.filters);
                            }
                          }
                        }}
                        placeholder="Select or create group"
                        showSearch
                        allowClear
                        optionLabelProp="rawLabel"
                        optionFilterProp="rawLabel"
                      />
                    </TooltipTriggerStyled>
                  </SbTooltip>
                )}
              </GroupControlWrapper>

              <EvaluateButtonWrapper ref={evaluateRef}>
                <SbTooltip content={evaluateButtonTooltip}>
                  <TooltipTriggerStyled>
                    <SbButton
                      buttonType="primary"
                      onClick={handlePAICPotentialClick}
                      disabled={isEvaluateDisabled}
                    >
                      {evaluateButtonLabel}
                    </SbButton>
                  </TooltipTriggerStyled>
                </SbTooltip>
              </EvaluateButtonWrapper>
            </ControlsRow>

            {paicProgress && (
              <PAICProgressPanel $failed={paicProgress.failed}>
                <PAICProgressHeader $failed={paicProgress.failed}>
                  <span>{paicTask?.projectName || selectedProjectGroup || "PAIC evaluation"}</span>
                  <span>{Math.max(0, Math.min(Number(paicProgress.progress) || 0, 100))}%</span>
                </PAICProgressHeader>
                <PAICProgressTrack>
                  <PAICProgressFill
                    $progress={paicProgress.progress}
                    $failed={paicProgress.failed}
                  />
                </PAICProgressTrack>
                <PAICProgressMeta $failed={paicProgress.failed}>
                  {paicProgress.status || paicProgress.event || "Processing PAIC background task..."}
                </PAICProgressMeta>
              </PAICProgressPanel>
            )}
          </HeaderActionsPanel>
        </HeaderTopRow>
      </HeaderSection>

      <TableSection ref={rolesTableRef}>
        {isRolesEmpty ? (
          <div ref={emptyStateRef}>
            <SbEmpty description="No PAIC groups yet. Create one above, select the roles you want to analyze, and run your first evaluation." />
          </div>
        ) : (
          <PAICRequisitionTableV2
            loading={loading}
            filteredJobs={filteredJobs}
            tableFilters={filters}
            onTableFiltersChange={setFilters}
          />
        )}
      </TableSection>
    </PageContainer>

    <ManageGroupsModal
      open={manageGroupsOpen}
      onClose={() => setManageGroupsOpen(false)}
      ownedProjects={ownedProjects}
      delegatedProjects={delegatedProjects}
      sharedProjects={sharedProjects}
      onRefresh={fetchPAICProjects}
      onSelectGroup={handleSelectGroupFromModal}
    />

    {/* ── Guided tour (Welcome + Finish are centered steps, no modal) ── */}
    <SbTour
      key={tourRunId}
      open={tourOpen}
      current={tourStep}
      onChange={changeTourStep}
      steps={builtTourSteps}
      onClose={closeTour}
      actionsRender={renderTourActions}
      indicatorsRender={renderTourIndicators}
    />
    </>
  );
};

export default PeoplePlusAIV2;